import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';

import { PendingPersistence } from './pipeline-category.state';

/**
 * A node in the staged containment tree. Both a method type and a resource can
 * contain resources, so the union shares `id` + `childIds` and discriminates on
 * `kind` (the root's `kind` records the root entity type — `method-type` for the
 * method-type editor, `resource` for the future resource editor).
 *
 * `childIds` lists the direct children and is meaningful once `childrenLoaded`
 * is `true`. A node upserted as someone's child starts `childrenLoaded: false`
 * (we know it exists, not yet its children); the lazy-load effect flips it.
 *
 * `pendingPersistence` is the staging ledger
 * `'save'` = created in the UI, not yet persisted — its `id` is a client-generated placeholder
 * `'update'` = persisted but locally edited
 * `undefined` = clean
 */
export type ResourceContainer =
  | {
      kind: 'method-type';
      id: string;
      childIds: string[];
      childrenLoaded: boolean;
      pendingPersistence: PendingPersistence;
    }
  | {
      kind: 'resource';
      id: string;
      name: string;
      childIds: string[];
      childrenLoaded: boolean;
      pendingPersistence: PendingPersistence;
    };

export type RootDescriptor =
  | { kind: 'method-type'; id: string; pendingPersistence?: PendingPersistence }
  | { kind: 'resource'; id: string; name: string; pendingPersistence?: PendingPersistence };

export interface ResourceChildDescriptor {
  id: string;
  name: string;
}

interface AssignedResourcesStateModel {
  containers: Record<string, ResourceContainer>;
  childrenLoadingFor: Record<string, boolean>;
  /*
   * Last lazy-load failure (cleared to `null` when a new attempt starts). Holds
   * the raw exception — mapping to an i18n key is the consuming facade's job.
   */
  childrenLoadError: { containerId: string; error: Error } | null;
}

const INITIAL_STATE: AssignedResourcesStateModel = {
  containers: {},
  childrenLoadingFor: {},
  childrenLoadError: null,
};

/**
 * Shared, staged containment tree for the resources manager. One instance is
 * provided by the outermost consumer and shared down the (future) dialog stack;
 * every read/write is keyed by container id, so a nested manager operating on a
 * deeper node uses the same instance. Nothing here is persisted
 */
@Injectable()
export class AssignedResourcesState extends ComponentStore<AssignedResourcesStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly containers$ = this.select((s) => s.containers);
  public readonly childrenLoadError$ = this.select((s) => s.childrenLoadError);

  /** The direct children (as full nodes) of the given container; `[]` if unknown. */
  public childrenOf(id: string): Observable<ResourceContainer[]> {
    return this.select((s) => {
      const container = s.containers[id];
      if (!container) return [];
      return container.childIds
        .map((childId) => s.containers[childId])
        .filter((node): node is ResourceContainer => node !== undefined);
    });
  }

  /** Whether the given container's children have been loaded/seeded. */
  public childrenLoadedOf(id: string): Observable<boolean> {
    return this.select((s) => s.containers[id]?.childrenLoaded ?? false);
  }

  /** Whether a lazy-load is in flight for the given container. */
  public isLoadingChildrenOf(id: string): Observable<boolean> {
    return this.select((s) => s.childrenLoadingFor[id] ?? false);
  }

  // ============================================================================
  // Updaters
  // ============================================================================

  /**
   * Seed a root container (`childrenLoaded: true`) plus its direct resource children
   */
  public readonly seed = this.updater(
    (state, payload: { root: RootDescriptor; children: ResourceChildDescriptor[] }) => {
      const childIds = payload.children.map((c) => c.id);
      const pendingPersistence = payload.root.pendingPersistence;
      const rootNode: ResourceContainer =
        payload.root.kind === 'method-type'
          ? { kind: 'method-type', id: payload.root.id, childIds, childrenLoaded: true, pendingPersistence }
          : {
              kind: 'resource',
              id: payload.root.id,
              name: payload.root.name,
              childIds,
              childrenLoaded: true,
              pendingPersistence,
            };

      const containers: Record<string, ResourceContainer> = { ...state.containers, [rootNode.id]: rootNode };
      for (const child of payload.children) {
        if (!containers[child.id]) containers[child.id] = this.makeResourceNode(child);
      }
      return { ...state, containers };
    }
  );

  /**
   * Stage a brand-new (not yet persisted) resource as a child of a container.
   * The draft's id is a client-generated placeholder (uuid).
   * Born childrenLoaded: true` — there is nothing in the DB to load for it.
   */
  public readonly stageDraft = this.updater((state, payload: { parentId: string; draft: ResourceChildDescriptor }) => {
    const parent = state.containers[payload.parentId];
    if (!parent) return state;

    const containers = { ...state.containers };
    if (!containers[payload.draft.id]) {
      containers[payload.draft.id] = {
        kind: 'resource',
        id: payload.draft.id,
        name: payload.draft.name,
        childIds: [],
        childrenLoaded: true,
        pendingPersistence: 'save',
      };
    }
    if (!parent.childIds.includes(payload.draft.id)) {
      containers[payload.parentId] = { ...parent, childIds: [...parent.childIds, payload.draft.id] };
    }
    return { ...state, containers };
  });

  /**
   * Rewrite placeholder ids to their persisted counterparts:
   * map keys, node ids and every `childIds` reference follow the map, loading flags are
   * re-keyed, and remapped nodes drop their `'save'` flag — a remap only ever
   * follows a successful persistence. Ids not in the map are untouched.
   */
  public readonly remapIds = this.updater((state, idMap: Record<string, string>) => {
    const containers: Record<string, ResourceContainer> = {};
    for (const node of Object.values(state.containers)) {
      const remappedId = idMap[node.id];
      const next: ResourceContainer = {
        ...node,
        id: remappedId ?? node.id,
        childIds: node.childIds.map((childId) => idMap[childId] ?? childId),
        pendingPersistence: remappedId ? undefined : node.pendingPersistence,
      };
      containers[next.id] = next;
    }
    const childrenLoadingFor: Record<string, boolean> = {};
    for (const [containerId, loading] of Object.entries(state.childrenLoadingFor)) {
      childrenLoadingFor[idMap[containerId] ?? containerId] = loading;
    }
    return { ...state, containers, childrenLoadingFor };
  });

  /** Lazy-load result: upsert child nodes and flip the container's `childIds` + `childrenLoaded`. */
  public readonly setChildren = this.updater(
    (state, payload: { containerId: string; children: ResourceChildDescriptor[] }) => {
      const container = state.containers[payload.containerId];
      if (!container) return state;

      const containers = { ...state.containers };
      for (const child of payload.children) {
        if (!containers[child.id]) containers[child.id] = this.makeResourceNode(child);
      }
      containers[payload.containerId] = {
        ...container,
        childIds: payload.children.map((c) => c.id),
        childrenLoaded: true,
      };
      return { ...state, containers };
    }
  );

  /** Stage a child under a container (idempotent; never clobbers an existing node). */
  public readonly assignTo = this.updater((state, payload: { parentId: string; child: ResourceChildDescriptor }) => {
    const parent = state.containers[payload.parentId];
    if (!parent) return state;

    const containers = { ...state.containers };
    if (!containers[payload.child.id]) containers[payload.child.id] = this.makeResourceNode(payload.child);
    if (!parent.childIds.includes(payload.child.id)) {
      containers[payload.parentId] = { ...parent, childIds: [...parent.childIds, payload.child.id] };
    }
    return { ...state, containers };
  });

  /**
   * Rename a resource node in place (display data only — relationships and
   * staging flags are untouched). No-op (same state reference) when the node is
   * missing, is the method-type root, or already carries the name, so repeated
   * dialog saves cannot churn `containers$`.
   */
  public readonly updateNodeName = this.updater((state, payload: { id: string; name: string }) => {
    const node = state.containers[payload.id];
    if (!node || node.kind !== 'resource' || node.name === payload.name) return state;
    return {
      ...state,
      containers: { ...state.containers, [payload.id]: { ...node, name: payload.name } },
    };
  });

  /** Un-stage a child from a container; prune the node if no container references it anymore. */
  public readonly removeFrom = this.updater((state, payload: { parentId: string; childId: string }) => {
    const parent = state.containers[payload.parentId];
    if (!parent) return state;

    const containers = { ...state.containers };
    containers[payload.parentId] = {
      ...parent,
      childIds: parent.childIds.filter((id) => id !== payload.childId),
    };

    const stillReferenced = Object.values(containers).some((node) => node.childIds.includes(payload.childId));
    if (!stillReferenced) delete containers[payload.childId];

    return { ...state, containers };
  });

  public readonly setChildrenLoading = this.updater((state, payload: { containerId: string; loading: boolean }) => ({
    ...state,
    childrenLoadingFor: { ...state.childrenLoadingFor, [payload.containerId]: payload.loading },
  }));

  public readonly setChildrenLoadError = this.updater(
    (state, childrenLoadError: { containerId: string; error: Error } | null) => ({ ...state, childrenLoadError })
  );

  public readonly reset = this.updater(() => ({ ...INITIAL_STATE }));

  private makeResourceNode(child: ResourceChildDescriptor): ResourceContainer {
    return {
      kind: 'resource',
      id: child.id,
      name: child.name,
      childIds: [],
      childrenLoaded: false,
      pendingPersistence: undefined,
    };
  }
}
